import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import DeviceCard from '../../../components/hardware/DeviceCard';
import { DeviceDetails } from '../../../components/hardware/DeviceDetails';
import DeviceActions from '../../../components/hardware/deviceActions';
import DeviceEventActions from '../../../components/hardware/deviceEventActions';
import DeviceInputs from '../../../components/hardware/deviceInputs';
import DeviceParameters from '../../../components/hardware/deviceParameters';
import BioEventActions from '../../../components/hardware/bioEventActions';
import Tabs from '../../../components/tabs/tabs';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Reader as ReaderModel } from '../../../models/Reader';
import { SimpleItemProfile } from '../../../models/SimpleItemProfile';
import { TabSummary } from '../../../models/TabSummary';
import { getDeviceProfileNames } from '../../../services/hardwarePresetService';
import { getReaderNamesWithPagination, getReadersById, storeReader } from '../../../services/readerService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import { PRESET_TYPES } from '../../../constants/preset-constants';
import { EABR_UNIT_TYPE_ID } from '../../../constants/device-types';
import { getDeviceInputTabCount } from '../../../services/deviceInputService';
import { updateTabBadgeOfImproxDevices } from '../../../utilities/tabUtils';
import { getBiometricDeviceByAssignedDeviceImprox } from '../../../services/biometricService';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware';
const itemPath = '/access-control/hardware/reader/';

const Reader: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.eventActions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.inputs), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [bioAddressId, setBioAddressId] = useState<string | undefined>(undefined);
    const [selectedEventActionsSubTab, setSelectedEventActionsSubTab] = useState(0);
    const eventActionsSubTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.eventActions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.biometricActions), badge: 0, showInitial: true, isActive: !!bioAddressId },
    ];

    const updateBadgeCount = async () => {
        try {
            if(itemId) {
                const deviceInputTabCount = await getDeviceInputTabCount(itemId);                
                setTabs(updateTabBadgeOfImproxDevices(tabs, deviceInputTabCount)); // need to make sure that we update the latest tab change
            }            
        }
        catch (error) {
            showError(error, toast.warn);
        }
    }

    const tabContentList: ContentWrapper[] = [{
        content: <DeviceDetails />,
    },
    {
        content: (
            <Tabs
                tabs={eventActionsSubTabs}
                selected={selectedEventActionsSubTab}
                onSelect={setSelectedEventActionsSubTab}
                add={false}
                onAdd={() => {}}
                isFlat={true}
                showBorder={false}>
                <hr />
                {selectedEventActionsSubTab === 0
                    ? <DeviceEventActions itemId={itemId} showProvidedSelectOptions={true} />
                    : bioAddressId
                        ? <BioEventActions terminalId={itemId} bioAddressId={bioAddressId} />
                        : null
                }
            </Tabs>
        ),
    },
    {
        content: <DeviceInputs itemId={itemId} />,
    },
    {
        content: <DeviceActions itemId={itemId} />,
    },
    {
        content: <DeviceParameters itemId={itemId} />,
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }];

    const topic: Topic = {
        accessor: 'devName',
        title: translate.byKey(TranslationKeys.readers),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const reloadPage = async () => {
        try {
            let reader;

            if (itemId) {
                reader = await getReadersById(itemId);
                setSelectedItem({label: reader.name, value: reader.id.toString()})
                setCurrentItem(reader);
                // update the tab badge counts
                updateBadgeCount();
                // Load the IDEMIA biometric attached to this reader, if any.
                // Only IDEMIA devices (stored as bio make "Sagem" in the DB) support LCD event/action messaging.
                try {
                    const bio = await getBiometricDeviceByAssignedDeviceImprox(itemId, 'Sagem');
                    setBioAddressId(bio?.value ?? undefined);
                } catch {
                    setBioAddressId(undefined);
                }
            }
            else {
                setTabs([initialTabs[1]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: itemPath + `${itemId}`, displayName: `${reader?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    useEffect(() => {
        // If biometric support is unavailable for the selected reader,
        // force the sub-tab back to Event Actions.
        if (!bioAddressId && selectedEventActionsSubTab !== 0) {
            setSelectedEventActionsSubTab(0);
        }
    }, [bioAddressId, selectedEventActionsSubTab]);

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];

        return selectedTabContent.content;
    };

    const editReader = (values) => {
        const newReader: ReaderModel = {
            ...currentItem,
            description: values.description,
            name: values.name,
            id: values.id,
            enabled: values.enabled,
            selectedPresetId: values.presetId,
            defaultModeId: values.defaultModeId,
            defaultMode:values.defaultMode,
            threatLevelId: values.threatLevelId,
            functionId: values.functionId,
            biometricInput: values.biometricInput,
            musteringPoint: values.musteringPoint,
            devOss: values.devOss
        }

        setBusy(true);
        storeReader(newReader, false)
            .then(async (value:any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.readerSaved));
                await reloadPage();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                // Will need to call this when updating the Preset of the Reader
                updateBadgeCount();
                setBusy(false);
            });
    }

    return <TopicContextProvider
        itemId={itemId}
        dataMethod={getReaderNamesWithPagination}
        topic={topic}
        selectedItem={selectedItem} >
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={editReader}
                tabs={tabs}
                initialValues={currentItem}>
                <DeviceCard type='READER'
                    dataMethod={(listParameters?: ListQuery) => getDeviceProfileNames(
                        currentItem!['typeId'] === EABR_UNIT_TYPE_ID ? PRESET_TYPES.BIOMETRIC : PRESET_TYPES.READER,
                        {
                            ...listParameters, filters: appendFilter(siteId ?? siteId, 'Site id', 'site.id', listParameters?.filters ?? [])
                        })}
                />
            </SimpleItemDetails>
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Reader;
